import { Link } from '@tanstack/react-router';
import { Avatar, AvatarFallback, AvatarImage } from '@ui/avatar.tsx';
import { Button } from '@ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@ui/dialog.tsx';
import { CircleAlert, Building2, Pencil, Users, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UpdateCompanyForm } from './update-company-form.tsx';
import { UserFullname } from '@r/components/user-fullname.tsx';
import type { CompanyCompanyReadModel } from '@r/api/generated/client.schemas.ts';

type CompanyDetailsGridProps = {
  company: CompanyCompanyReadModel;
  modalState: {
    isCompanyInfoOpen: boolean;
    isResponsibleOpen: boolean;
  };
  handlers: {
    setCompanyInfoOpen: (open: boolean) => void;
    setResponsibleOpen: (open: boolean) => void;
  };
};

export function CompanyDetailsGrid({ company, modalState, handlers }: CompanyDetailsGridProps) {
  const { t } = useTranslation();
  const responsibles = company.responsibles ?? [];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
      <Card className="w-full lg:col-span-2">
        <CardHeader className="p-10 pb-4">
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center space-x-2 text-xl font-author-complete font-regular">
              <Building2 />
              <p className="text-xl font-bold">{t('admin.company.profile.section.company')}</p>
            </CardTitle>
            <Dialog open={modalState.isCompanyInfoOpen} onOpenChange={handlers.setCompanyInfoOpen}>
              <DialogTrigger asChild>
                <Button variant="link" className="px-0 h-auto text-primary">
                  <Pencil />
                  {t('common.edit')}
                </Button>
              </DialogTrigger>
              <DialogContent className="overflow-hidden p-0 max-w-5xl">
                <div className="p-10 max-h-[90vh] overflow-y-auto scrollable">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">
                      {t('admin.company.profile.title')}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="mt-4">
                    <UpdateCompanyForm
                      {...company}
                      showResponsibleField={false}
                      onSuccess={() => handlers.setCompanyInfoOpen(false)}
                    />
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-10 pt-0 space-y-8">
          <div className="space-y-2">
            <p className="font-semibold">{t('admin.company.profile.section.legal.info')}</p>
            <div className="h-px bg-border" />
            <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-y-4 text-base">
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.company.siren')}
              </p>
              <p className="text-sm">{company.siren || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">{t('label.company.tva')}</p>
              <p className="text-sm">{company.tva || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.company.unique.code')}
              </p>
              <p className="text-sm">{company.uniqueCode || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.company.typology')}
              </p>
              <p className="text-sm">{company.typology?.name || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.company.created.at')}
              </p>
              <p className="text-sm">{new Date(company.createdAt).toLocaleDateString('fr-FR')}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.company.comment')}
              </p>
              <p className="min-w-0 text-sm whitespace-pre-wrap [overflow-wrap:anywhere]">
                {company.comment || '-'}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="font-semibold">{t('admin.company.profile.section.postal.info')}</p>
            <div className="h-px bg-border" />
            <div className="grid grid-cols-[160px_1fr] gap-y-4 text-base">
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.company.address.line.1')}
              </p>
              <p className="text-sm">{company.addressLine1 || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.company.address.line.2')}
              </p>
              <p className="text-sm">{company.addressLine2 || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.company.address.line.3')}
              </p>
              <p className="text-sm">{company.addressLine3 || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.company.zipcode')}
              </p>
              <p className="text-sm">{company.zipcode || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">{t('label.company.city')}</p>
              <p className="text-sm">{company.city || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.company.country')}
              </p>
              <p className="text-sm">{company.country?.name || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="flex flex-col lg:col-span-1">
        <CardHeader className="p-6 pb-3">
          <CardTitle className="flex gap-2 items-center text-4xl font-bold">
            <Users />
            <span className="text-xl font-bold">{t('admin.company.profile.section.contacts')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col flex-1 p-6 pt-0">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="font-semibold">{t('admin.company.profile.contacts.collaborators')}</p>
            </div>
            <div className="h-px bg-border" />
            {company.userWorkspaces?.length ? (
              <div
                className={
                  company.userWorkspaces.length > 5 ? 'max-h-[325px] overflow-y-auto pr-1' : 'pr-1'
                }
              >
                <div className="space-y-2">
                  {company.userWorkspaces.map((workspaceUser, index) => {
                    const collaborator = workspaceUser.user;
                    if (!collaborator) return null;
                    return collaborator.id ? (
                      <Link
                        key={`${collaborator.id}-${index}`}
                        to="/users/$id"
                        params={{ id: String(collaborator.id) }}
                        className="flex justify-between items-center px-3 py-2 rounded-lg border transition-colors bg-background hover:bg-accent/40"
                      >
                        <div className="flex gap-3 items-center">
                          <Avatar size="nav">
                            <AvatarImage
                              src={collaborator.avatar ?? ''}
                              alt={collaborator.fullname ?? ''}
                            />
                            <AvatarFallback text={collaborator.fullname ?? ''} initials={true} />
                          </Avatar>
                          <div className="leading-tight">
                            <p className="text-sm font-semibold">
                              <UserFullname
                                fullname={collaborator.fullname ?? ''}
                                isDeleted={collaborator.isDeleted}
                              />
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {workspaceUser.uniqueCode ?? '-'}
                            </p>
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-muted-foreground" />
                      </Link>
                    ) : (
                      <div
                        key={`${workspaceUser.uniqueCode ?? 'workspace-user'}-${index}`}
                        className="flex gap-3 items-center px-3 py-2 rounded-lg border bg-background"
                      >
                        <Avatar size="nav">
                          <AvatarImage
                            src={collaborator.avatar ?? ''}
                            alt={collaborator.fullname ?? ''}
                          />
                          <AvatarFallback text={collaborator.fullname ?? ''} initials={true} />
                        </Avatar>
                        <div className="leading-tight">
                          <p className="text-sm font-semibold">{collaborator.fullname || '-'}</p>
                          <p className="text-sm text-muted-foreground">
                            {workspaceUser.uniqueCode ?? '-'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2 items-center">
                <span>
                  <CircleAlert className="text-primary" />
                </span>
                <p className="text-sm text-center text-muted-foreground">
                  {t('admin.company.profile.contacts.empty.collaborators')}
                </p>
              </div>
            )}
          </div>

          <div className="mt-2 space-y-2">
            <div className="flex justify-between items-center">
              <p className="font-semibold">{t('label.company.responsible')}</p>
              <Dialog
                open={modalState.isResponsibleOpen}
                onOpenChange={handlers.setResponsibleOpen}
              >
                <DialogTrigger asChild>
                  <Button variant="link" className="px-0 h-auto text-primary">
                    <Pencil />
                    {t('common.edit')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="overflow-hidden p-0 max-w-3xl">
                  <div className="p-8 max-h-[90vh] overflow-y-auto scrollable">
                    <DialogHeader>
                      <DialogTitle className="text-2xl">
                        {t('label.company.responsible')}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="mt-4">
                      <UpdateCompanyForm
                        {...company}
                        formMode="responsible-only"
                        onSuccess={() => handlers.setResponsibleOpen(false)}
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="h-px bg-border" />
            {responsibles.length > 0 ? (
              <div
                className={responsibles.length > 5 ? 'max-h-[325px] overflow-y-auto pr-1' : 'pr-1'}
              >
                <div className="space-y-2">
                  {responsibles.map((responsible, index) => {
                    const responsibleUser = responsible.user;

                    if (!responsibleUser) return null;

                    const content = (
                      <>
                        <div className="flex gap-3 items-center min-w-0">
                          <Avatar size="nav">
                            <AvatarImage
                              src={responsibleUser.avatar ?? ''}
                              alt={responsibleUser.fullname ?? ''}
                            />
                            <AvatarFallback text={responsibleUser.fullname ?? ''} initials={true} />
                          </Avatar>
                          <div className="min-w-0 leading-tight">
                            <p className="text-sm font-semibold truncate">
                              <UserFullname
                                fullname={responsibleUser.fullname ?? ''}
                                isDeleted={responsibleUser.isDeleted}
                              />
                            </p>
                            <div className="flex flex-wrap gap-1 items-center">
                              <p className="text-sm text-muted-foreground">
                                {responsible.uniqueCode ?? '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                        {responsibleUser.id ? (
                          <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
                        ) : null}
                      </>
                    );

                    return responsibleUser.id ? (
                      <Link
                        key={`${responsibleUser.id}-${index}`}
                        to="/users/$id"
                        params={{ id: String(responsibleUser.id) }}
                        className="flex gap-3 justify-between items-center px-3 py-2 rounded-lg border transition-colors bg-background hover:bg-accent/40"
                      >
                        {content}
                      </Link>
                    ) : (
                      <div
                        key={`${responsible.uniqueCode ?? 'responsible'}-${index}`}
                        className="flex gap-3 justify-between items-center px-3 py-2 rounded-lg border bg-background"
                      >
                        {content}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2 items-center">
                <span>
                  <CircleAlert className="text-primary" />
                </span>
                <p className="text-sm text-center text-muted-foreground">
                  {t('admin.company.profile.contacts.empty.responsible')}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
